import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  output,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router'

import { Store } from '@ngrx/store'
import { isEqual } from 'lodash'
import { MenuItem } from 'primeng/api'
import { BreadcrumbModule } from 'primeng/breadcrumb'
import { TagModule } from 'primeng/tag'

import { distinctUntilChanged, filter, map } from 'rxjs'

import { FlxModalModule } from '@flowx/angular-ui-toolkit'
import {
  WorkflowInstanceDisplayContext,
  instanceStatusTagTypeMap,
} from '@flowx/designer/modules/integration-designer/models/workflow-instance.entity'
import { WorkflowInstanceDetailsStore } from '@flowx/designer/modules/integration-designer/workflow-integration/components/workflow-run-instance/workflow-instance-details.store'
import { QueryParamsService } from '@flowx/designer/shared/services/query-params.service'
import { selectAppId } from '@flowx/designer/shared/store/selectors/router.selectors'

@Component({
  selector: 'flx-workflow-instance-details-modal',
  providers: [WorkflowInstanceDetailsStore],
  imports: [FlxModalModule, TagModule, RouterOutlet, BreadcrumbModule],
  templateUrl: './workflow-instance-details-modal.component.html',
  host: { class: 'w-full h-full' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowInstanceDetailsModalComponent implements OnInit {
  #queryParamsService = inject(QueryParamsService)
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #store = inject(Store)
  #destroyRef = inject(DestroyRef)
  #workflowInstanceDetailsStore = inject(WorkflowInstanceDetailsStore)

  workflowInstanceId = signal<string>('')
  workflowName = signal<string>('')
  appId = signal<string>('')

  hideModal = output()

  status = this.#workflowInstanceDetailsStore.status
  instanceStatusTagTypeMap = instanceStatusTagTypeMap

  breadcrumbs = signal<MenuItem[]>([])

  ngOnInit(): void {
    this.#route.queryParams
      .pipe(
        map((params) => this.#queryParamsService.decode(params)?.workflowInstances),
        distinctUntilChanged(isEqual),
        filter((workflowInstances) => workflowInstances?.length > 0),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe((workflowInstances) => {
        this.breadcrumbs.set(this.#buildBreadcrumbMenuItems(workflowInstances))

        const mainWorkflow = workflowInstances[0]
        this.workflowInstanceId.set(mainWorkflow.workflowInstanceId)
        this.workflowName.set(mainWorkflow.workflowName)
        this.appId.set(mainWorkflow.appId ?? this.#store.selectSignal(selectAppId)())

        const currentWorkflowInstance = workflowInstances[workflowInstances.length - 1]
        this.#workflowInstanceDetailsStore.getWorkflowInstance(
          currentWorkflowInstance.workflowInstanceId,
          currentWorkflowInstance.appId ?? this.appId()
        )
      })
    this.#workflowInstanceDetailsStore.setContext(WorkflowInstanceDisplayContext.RUNTIME)
  }

  #buildBreadcrumbMenuItems(workflowInstances: any): MenuItem[] {
    return workflowInstances.map((workflowInstance: any) => ({
      label: workflowInstance.workflowName,
      command: () => {
        const remainingWorkflowInstances = workflowInstances.slice(
          0,
          workflowInstances.indexOf(workflowInstance) + 1
        )
        this.#router.navigate([], {
          relativeTo: this.#route,
          queryParams: this.#queryParamsService.encode({
            workflowInstances: remainingWorkflowInstances,
          }),
          queryParamsHandling: 'merge',
        })
      },
    }))
  }
}
